import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";

import {
  getGetStoreInvitesQueryKey,
  getGetStoreMembersQueryKey,
  useCreateStoreInvite,
  useDeleteStoreInvite,
  useGetStoreInvites,
  useGetStoreMembers,
  useGetTeamRoles,
  useRemoveStoreMember,
  useUpdateStoreMemberRole,
} from "@/api";
import { getApiErrorMessage } from "@/lib/api/errors";
import {
  getInvitableRoleDefinitions,
  getRoleDefinition,
  resolveRoleKeyFromRoleName,
} from "@/lib/settings/access";
import type { ShopRoleKey } from "@/lib/settings/constants";

const IR_MOBILE = /^09\d{9}$/;

export type TeamConfirm =
  | { kind: "removeMember"; userId: number; label: string }
  | { kind: "cancelInvite"; inviteId: number; label: string };

export type TeamMemberView = {
  userId: number;
  displayName: string;
  phone: string;
  roleKey: ShopRoleKey;
  isOwner: boolean;
  isYou: boolean;
};

export type TeamInviteView = {
  id: number;
  phone: string;
  roleKey: ShopRoleKey;
  isExpired: boolean;
  daysLeft: number;
  inviteUrl: string;
};

/** Team tab: real members with per-store roles, pending invites, and the invite form. */
export function useSettingsTeam(storeId: number | null, enabled: boolean, currentUserId?: number) {
  const queryClient = useQueryClient();
  const active = enabled && storeId !== null;
  const id = storeId ?? 0;

  const { data: membersData, isLoading: isMembersLoading } = useGetStoreMembers(id, {
    query: { enabled: active },
  });
  const { data: invitesData, isLoading: isInvitesLoading } = useGetStoreInvites(id, {
    query: { enabled: active },
  });
  const { data: rolesData } = useGetTeamRoles({ query: { enabled: active, staleTime: Infinity } });

  const roleIdByKey = useMemo(() => {
    const roles = rolesData?.status === 200 ? rolesData.data.data : [];
    const map = new Map<ShopRoleKey, number>();
    for (const role of roles) {
      const key = resolveRoleKeyFromRoleName(role.name);
      if (key && key !== "owner") map.set(key, role.id);
    }
    return map;
  }, [rolesData]);

  const members: TeamMemberView[] = useMemo(() => {
    const rows = membersData?.status === 200 ? membersData.data.data : [];
    return rows.map((member) => ({
      userId: member.user_id,
      displayName: member.full_name?.trim() || member.phone,
      phone: member.phone,
      roleKey: member.is_owner
        ? "owner"
        : (resolveRoleKeyFromRoleName(member.role_name) ?? "operator"),
      isOwner: member.is_owner,
      isYou: member.user_id === currentUserId,
    }));
  }, [currentUserId, membersData]);

  // Captured once per mount; days-left only needs day precision.
  const [now] = useState(() => Date.now());

  const invites: TeamInviteView[] = useMemo(() => {
    const rows = invitesData?.status === 200 ? invitesData.data.data : [];
    return rows.map((invite) => ({
      id: invite.id,
      phone: invite.phone,
      roleKey: resolveRoleKeyFromRoleName(invite.role_name) ?? "operator",
      isExpired: invite.is_expired,
      daysLeft: Math.max(
        0,
        Math.ceil((new Date(invite.expires_at).getTime() - now) / 86_400_000),
      ),
      inviteUrl: invite.invite_url,
    }));
  }, [invitesData, now]);

  const refresh = useCallback(() => {
    if (storeId === null) return;
    void queryClient.invalidateQueries({ queryKey: getGetStoreMembersQueryKey(storeId) });
    void queryClient.invalidateQueries({ queryKey: getGetStoreInvitesQueryKey(storeId) });
  }, [queryClient, storeId]);

  // ----- Invite form -----
  const [invitePhone, setInvitePhone] = useState("");
  const [inviteRoleKey, setInviteRoleKey] = useState<ShopRoleKey | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [lastInvite, setLastInvite] = useState<{ phone: string; url: string } | null>(null);
  const phoneTouched = invitePhone.length > 0;
  const phoneValid = IR_MOBILE.test(invitePhone);

  const { mutate: createInvite, isPending: isCreatingInvite } = useCreateStoreInvite({
    mutation: {
      onSuccess: (res, variables) => {
        if (res.status !== 201) return;
        setLastInvite({ phone: variables.data.phone, url: res.data.data.invite_url });
        setInvitePhone("");
        setInviteRoleKey(null);
        refresh();
      },
      onError: (error) => setInviteError(getApiErrorMessage(error) ?? "ارسال دعوت‌نامه انجام نشد"),
    },
  });

  const onSendInvite = useCallback(() => {
    if (storeId === null || inviteRoleKey === null || !phoneValid) return;
    const roleId = roleIdByKey.get(inviteRoleKey);
    if (!roleId) return;
    setInviteError(null);
    setLastInvite(null);
    createInvite({ storeId, data: { phone: invitePhone, role_id: roleId } });
  }, [createInvite, invitePhone, inviteRoleKey, phoneValid, roleIdByKey, storeId]);

  const roleCards = useMemo(
    () =>
      getInvitableRoleDefinitions().map((definition) => ({
        ...definition,
        selected: inviteRoleKey === definition.key,
        disabled: !roleIdByKey.has(definition.key),
        onSelect: () => {
          setInviteError(null);
          setInviteRoleKey((current) => (current === definition.key ? null : definition.key));
        },
      })),
    [inviteRoleKey, roleIdByKey],
  );

  // ----- Member role change / removal / invite cancel -----
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<TeamConfirm | null>(null);
  const onActionError = (error: unknown) =>
    setActionError(getApiErrorMessage(error) ?? "عملیات انجام نشد؛ دوباره تلاش کنید");

  const { mutate: updateRole, isPending: isUpdatingRole } = useUpdateStoreMemberRole({
    mutation: { onSuccess: refresh, onError: onActionError },
  });
  const { mutate: removeMember, isPending: isRemovingMember } = useRemoveStoreMember({
    mutation: {
      onSuccess: () => {
        setConfirm(null);
        refresh();
      },
      onError: (error) => {
        setConfirm(null);
        onActionError(error);
      },
    },
  });
  const { mutate: cancelInvite, isPending: isCancellingInvite } = useDeleteStoreInvite({
    mutation: {
      onSuccess: () => {
        setConfirm(null);
        refresh();
      },
      onError: (error) => {
        setConfirm(null);
        onActionError(error);
      },
    },
  });

  const onChangeMemberRole = useCallback(
    (userId: number, roleKey: ShopRoleKey) => {
      const roleId = roleIdByKey.get(roleKey);
      if (storeId === null || !roleId) return;
      setActionError(null);
      updateRole({ storeId, userId, data: { role_id: roleId } });
    },
    [roleIdByKey, storeId, updateRole],
  );

  const onConfirm = useCallback(() => {
    if (storeId === null || !confirm) return;
    setActionError(null);
    if (confirm.kind === "removeMember") removeMember({ storeId, userId: confirm.userId });
    else cancelInvite({ storeId, inviteId: confirm.inviteId });
  }, [cancelInvite, confirm, removeMember, storeId]);

  const roleOptions = useMemo(
    () =>
      getInvitableRoleDefinitions()
        .filter((definition) => roleIdByKey.has(definition.key))
        .map((definition) => ({ value: definition.key, labelKey: definition.labelKey })),
    [roleIdByKey],
  );

  return {
    members,
    invites,
    isLoading: isMembersLoading || isInvitesLoading,
    roleCards,
    roleOptions,
    invitePhone,
    onInvitePhoneChange: (value: string) => {
      setInviteError(null);
      setInvitePhone(value.replace(/\D/g, "").slice(0, 11));
    },
    phoneError: phoneTouched && !phoneValid && invitePhone.length >= 11 ? "invalid" : null,
    canSendInvite: phoneValid && inviteRoleKey !== null && !isCreatingInvite,
    isCreatingInvite,
    inviteError,
    lastInvite,
    onSendInvite,
    onChangeMemberRole,
    isUpdatingRole,
    actionError,
    confirm,
    isConfirmPending: isRemovingMember || isCancellingInvite,
    onAskRemoveMember: (member: TeamMemberView) =>
      setConfirm({ kind: "removeMember", userId: member.userId, label: member.displayName }),
    onAskCancelInvite: (invite: TeamInviteView) =>
      setConfirm({ kind: "cancelInvite", inviteId: invite.id, label: invite.phone }),
    onConfirm,
    onCloseConfirm: () => setConfirm(null),
    getRoleDefinition,
  };
}

export type SettingsTeamState = ReturnType<typeof useSettingsTeam>;
